import React, { useState, useCallback, useEffect } from 'react';
import { keyframes } from '@mui/system';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Collapse,
  Tab,
  Tabs,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import AnnotationListPanel from '../../../components/chart/AnnotationListPanel';

const STORAGE_KEY = 'marketDetail.collapsedSymbolGroups';

// Helper to get initial collapsed tags from localStorage
const getInitialCollapsedTags = () => {
  if (typeof window === 'undefined') {
    return new Set();
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch (error) {
    return new Set();
  }
};

// Shimmer animation for loading skeleton
const sidebarShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const SymbolSidebar = ({
  groupedSymbols = [],
  symbolCount,
  annotations = [],
  selectedAnnotationId,
  onSelectSymbol,
  onAnnotationSelect,
  onToggleAnnotationVisibility,
  loading = false,
  error = false,
  errorMessage,
  retryCallback,
  currentRouteSymbol,
  sidebarTab,
  setSidebarTab,
  collapsedAnnotationTypes,
  setCollapsedAnnotationTypes,
  // symbol (URL form) → Cycle Clock stage ({ key, label, token }); optional (DWLF-300).
  stages = null,
}) => {
  const [collapsedTags, setCollapsedTags] = useState(getInitialCollapsedTags);

  // Persist collapsed tags to localStorage
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(collapsedTags)),
      );
    } catch (error) {
      // If storage fails, fall back to in-memory state only.
    }
  }, [collapsedTags]);

  const handleSidebarTabChange = useCallback((event, newValue) => {
    setSidebarTab(newValue);
  }, []);

  const handleToggleTag = useCallback((tag) => {
    setCollapsedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }, []);

  const handleToggleAnnotationType = useCallback((type) => {
    setCollapsedAnnotationTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleAnnotationSelect = useCallback(
    (annotation) => {
      if (onAnnotationSelect) {
        onAnnotationSelect(annotation);
      }
    },
    [onAnnotationSelect],
  );

  const handleToggleAnnotationVisibility = useCallback(
    (annotation) => {
      if (onToggleAnnotationVisibility) {
        onToggleAnnotationVisibility(annotation);
      }
    },
    [onToggleAnnotationVisibility],
  );

  // Render error state
  if (error && groupedSymbols.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 1.5,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="error">
          Failed to load symbols
        </Typography>
        {errorMessage && (
          <Typography variant="caption" color="text.secondary">
            {typeof errorMessage === 'string' ? errorMessage : 'An error occurred'}
          </Typography>
        )}
        {retryCallback && (
          <Box
            component="button"
            onClick={retryCallback}
            sx={{
              mt: 0.5,
              px: 2,
              py: 0.75,
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 1,
              backgroundColor: 'transparent',
              color: 'primary.main',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(144, 202, 249, 0.08)',
              },
            }}
          >
            Retry
          </Box>
        )}
      </Box>
    );
  }

  // Render loading skeletons
  if (loading) {
    return (
      <Box sx={{ px: 1, py: 1.5 }}>
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              height: 28,
              mb: 0.75,
              mx: 1,
              borderRadius: 0.5,
              backgroundColor: 'var(--dwlf-ink-06)',
              backgroundImage: 'linear-gradient(90deg, transparent, var(--dwlf-ink-08), transparent)',
              backgroundSize: '200% 100%',
              animation: `${sidebarShimmer} 1.5s ease-in-out infinite`,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </Box>
    );
  }

  // Render empty state
  if (groupedSymbols.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No symbols available
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Tabs
        value={sidebarTab}
        onChange={handleSidebarTabChange}
        variant="fullWidth"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
          '& .MuiTab-root': {
            minHeight: 40,
            py: 0.5,
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'none',
          },
        }}
      >
        <Tab
          label={`Symbols (${symbolCount ?? groupedSymbols.reduce((acc, g) => acc + g.symbols.length, 0)})`}
        />
        <Tab label={`Annotations (${annotations.length})`} />
      </Tabs>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {sidebarTab === 0 && (
          <List dense disablePadding>
            {groupedSymbols.map(({ tag, symbols: groupSymbols }) => (
              <React.Fragment key={tag}>
                <ListSubheader
                  disableSticky
                  onClick={() => handleToggleTag(tag)}
                  sx={{
                    backgroundColor: 'background.paper',
                    color: 'text.secondary',
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {
                      backgroundColor: (theme) => (theme.vars || theme).palette.action.hover,
                    },
                  }}
                >
                  <span>{tag}</span>
                  {collapsedTags.has(tag) ? (
                    <ExpandMore fontSize="inherit" />
                  ) : (
                    <ExpandLess fontSize="inherit" />
                  )}
                </ListSubheader>
                <Collapse
                  in={!collapsedTags.has(tag)}
                  timeout="auto"
                  unmountOnExit
                >
                  {groupSymbols.map((candidate) => {
                    const routeSymbol = `${candidate}`.replace('/', '-');
                    const selected = routeSymbol === currentRouteSymbol;
                    const stage = stages?.[routeSymbol.toUpperCase()];
                    const needsAttention = stage?.attention === true;
                    return (
                      <ListItemButton
                        key={`${tag}-${candidate}`}
                        selected={selected}
                        onClick={() => onSelectSymbol && onSelectSymbol(candidate)}
                      >
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box component="span" aria-hidden sx={{ width: 6, height: 6, flexShrink: 0, backgroundColor: stage ? `var(--dwlf-${stage.token})` : 'transparent' }} />
                              <Typography variant="body2" fontWeight={selected ? 700 : 500} component="span" sx={{ flexGrow: 1 }}>
                                {candidate}
                              </Typography>
                              {needsAttention && (
                                <Typography component="span" sx={{ fontFamily: '"Space Mono", monospace', fontSize: '0.5625rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: `var(--dwlf-${stage.token})` }}>
                                  {stage.label}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItemButton>
                    );
                  })}
                </Collapse>
              </React.Fragment>
            ))}
          </List>
        )}
        {sidebarTab === 1 && (
          <AnnotationListPanel
            annotations={annotations}
            selectedAnnotationId={selectedAnnotationId}
            onSelectAnnotation={handleAnnotationSelect}
            onToggleVisibility={handleToggleAnnotationVisibility}
            collapsedTypes={collapsedAnnotationTypes}
            onToggleTypeCollapse={handleToggleAnnotationType}
          />
        )}
      </Box>
    </>
  );
};

export default SymbolSidebar;
